import React, { createContext, useCallback, useEffect, useState } from "react";
import { useConfig } from "@genflow/core";
import useProfileUserData from "@genflow/web-auth/hooks/useProfileUserData";
import useCourse from "@genflow/web-courses/hooks/useCourse";
import { useCompletedSessions, useWorkout } from "@genflow/web-workouts";
import {get, flatten, orderBy, flattenDeep, groupBy, keys, merge, parseInt, uniqBy, values } from "lodash";
import transformWorkoutProductToClasses, { isRestDay } from "./utils/transformWorkoutProductToClasses";
import transformCourseToClasses from "./utils/transformCourseToClasses";

import Fuse from "fuse.js";
import { useUser } from "../../hooks/useUser";
import { GUIDE_FIND_WORKOUT_FREQUENCY } from "../../config";
import { CHALLENGES_PRODUCT_ID } from "../../config";
import moment from "moment";

export const ContentManagerContext = createContext();
const isBrowser = typeof window !== `undefined`;

const ContentManagerProvider = ({ children }) => {
  const { authenticated, activeSubscription } = useUser();

  const {
    vendorUuid,
    GUIDE_MAP,
    // GUIDES_ORDER,
    PHYSIO_COURSE_PRODUCT_ID,
    LEARN_EXERCISES_COURSE_PRODUCT_ID,
    NUTRITION_COURSE_PRODUCT_ID,
    FREE_BANDS_EDUCATION_COURSE_PRODUCT_ID,
  } = useConfig();

  const { data: profileUserData, loading: profileUserDataLoading } = useProfileUserData();
  // Getting the users current selected guide on their app
  const profileData = JSON.parse(profileUserData?.data || "{}");
  const currentGuideProductID = profileData?.activeWorkout;

  const { data: guideProduct, loading: currentGuideProductLoading } = useWorkout(currentGuideProductID, null, null, {
    subscriptionACL: true,
    skip: !authenticated || !currentGuideProductID,
  });

  const userWorkoutFrequency = !!currentGuideProductID && GUIDE_FIND_WORKOUT_FREQUENCY(currentGuideProductID);

  // // 7
  // console.log("TEST --->", userWorkoutFrequency);
  // // true
  // console.log(authenticated);

  // Courses TODO: Improve batch class fetch from backend as the solution below is expensive (but it works)!!
  const courseConfig = {
    subscriptionACL: true,
    skip: !authenticated || !userWorkoutFrequency,
  };
  const workoutsConfig = {
    subscriptionACL: true,
    skip: !authenticated || !userWorkoutFrequency,
  };

  const { data: physioData, loading: physioDataLoading } = useCourse(
    vendorUuid,
    PHYSIO_COURSE_PRODUCT_ID,
    courseConfig
  );
  const { data: learnExercisesData, loading: learnExercisesDataLoading } = useCourse(
    vendorUuid,
    LEARN_EXERCISES_COURSE_PRODUCT_ID,
    courseConfig
  );
  const { data: nutritionData, loading: nutritionLoading } = useCourse(
    vendorUuid,
    NUTRITION_COURSE_PRODUCT_ID,
    courseConfig
  );
  const { data: howToBandsData, loading: howToBandsDataLoading } = useCourse(
    vendorUuid,
    FREE_BANDS_EDUCATION_COURSE_PRODUCT_ID,
    courseConfig
  );

  const { data: challengesData, loading: challengesDataLoading } = useCourse(
    vendorUuid,
    CHALLENGES_PRODUCT_ID,
    courseConfig
  );

  // Only grabbing 3 or 4 or 5 or 6x version (same as users current guide) of each guide

  const { data: bootyWorkoutProduct, loading: bootyWorkoutProductLoading } = useWorkout(
    3095,
    null,
    null,
    workoutsConfig
  );
  const { data: hourglassWorkoutProduct, loading: hourglassWorkoutProductLoading } = useWorkout(
    3235,
    null,
    null,
    workoutsConfig
  );
  const { data: smallerWaistWorkoutProduct, loading: smallerWaistWorkoutProductLoading } = useWorkout(
    3100,
    null,
    null,
    workoutsConfig
  );
  const { data: sabriWorkoutProduct, loading: sabriWorkoutProductLoading } = useWorkout(
    3389,
    null,
    null,
    workoutsConfig
  );
  const { data: twentyOneWorkoutProduct, loading: twentyOneProductLoading } = useWorkout(
    3472,
    null,
    null,
    workoutsConfig
  );

  const { data: halloLeanWorkoutProduct, loading: halloLeanProductLoading } = useWorkout(
    3484,
    null,
    null,
    workoutsConfig
  );

  const { data: cutStudentWorkoutProduct, loading: cutStudentProductLoading } = useWorkout(
    3670,
    null,
    null,
    workoutsConfig
  );

  const { data: strongCapableWorkoutProduct, loading: strongCapableProductLoading } = useWorkout(
    4100,
    null,
    null,
    workoutsConfig
  );

  const { data: leanMasWorkoutProduct, loading: leanMasProductLoading } = useWorkout(4115, null, null, workoutsConfig);

  const { data: pilatesWorkoutProduct, loading: pilatesProductLoading } = useWorkout(4141, null, null, workoutsConfig);

  const { data: week4Product, loading: week4Loading } = useWorkout(4233, null, null, workoutsConfig);
  const { data: galentineProduct, loading: galentineLoading } = useWorkout(4269, null, null, workoutsConfig);
  const { data: turnItUpProduct, loading: turnItUpLoading } = useWorkout(4276, null, null, workoutsConfig);
  // const { data: maryBraunWeek1Product, loading: maryBraunWeek1Loading } = useWorkout(
  //   4453,
  //   null,
  //   null,
  //   workoutsConfig
  // );
  // const { data: maryBraunWeek2Product, loading: maryBraunWeek2Loading } = useWorkout(
  //   4470,
  //   null,
  //   null,
  //   workoutsConfig
  // );
  const { data: deloadPilatesBootCampProduct, loading: deloadPilatesBootCampLoading } = useWorkout(
    4444,
    null,
    null,
    workoutsConfig
  );

  const { data: preSummerBootcampProduct, loading: preSummerBootcampLoading } = useWorkout(
    4465,
    null,
    null,
    workoutsConfig
  );
  // const { data: maryBraunWeek3Product, loading: maryBraunWeek3Loading } = useWorkout(
  //   4521,
  //   null,
  //   null,
  //   workoutsConfig
  // );
  // const { data: maryBraunWeek4Product, loading: maryBraunWeek4Loading } = useWorkout(
  //   4526,
  //   null,
  //   null,
  //   workoutsConfig
  // );
  const { data: periodFriendlyProduct, loading: periodFriendlyLoading } = useWorkout(
    4570,
    null,
    null,
    workoutsConfig
  );


  const {data:bestOfSculptProduct, loading:bestOfSculptLoading}=useWorkout(
      4648,
      null,
      null,
      workoutsConfig,
  );

  const {data:bestOfShredProduct, loading: bestOfShredLoading}=useWorkout(
      4662,
      null,
      null,
      workoutsConfig
  );

  const {data:birthdayProduct, loading:birthdayLoading}=useWorkout(
      4699,
      null,
      null,
      workoutsConfig
  )





  const {data: gimmeGummyMoreProduct, loading:gimmeGummyMoreLoading}=useWorkout(
      4920,
      null,
      null,
      workoutsConfig
  );

  const {data:halloLeanPrepProduct, loading:halloLeanPrepLoading} = useWorkout(
      5126,
      null,
      null,
      workoutsConfig,
  );

  const {data:halloLeanHellWeek22Product, loading:halloLeanHellWeek22Loading} = useWorkout(
      5135,
  null,
  null,
  workoutsConfig
  );

  const {data:bestOfStrengthProduct, loading:bestOfStrengthLoading} = useWorkout(
      5160,
  null,
  null,
  workoutsConfig
  );

  const {data:pilatesBodyProduct, loading:pilatesBodyLoading} = useWorkout(
      5195,
      null,
      null,
      workoutsConfig
  );

  const {data:cardioGlowUpProduct, loading:cardioGlowUpLoading} = useWorkout(
      5280,
      null,
      null,
      workoutsConfig
  );

  const {data:leanAndPowerfulProduct, loading:leanAndPowerfulLoading} = useWorkout(
      5289,
      null,
      null,
      workoutsConfig
  );

  const {data:leanTransformationMethodPhase1Product, loading:leanTransformationMethodPhase1Loading} = useWorkout(
      5513,
      null,
      null,
      workoutsConfig
  );

  const {data:leanTransformationMethodPhase2Product, loading:leanTransformationMethodPhase2Loading} = useWorkout(
      5608,
      null,
      null,
      workoutsConfig
  );

  const {data:leanMass2022RepackedProduct, loading:leanMass2022RepackedLoading} = useWorkout(
      5474,
      null,
      null,
      workoutsConfig
  );

  const {data:bestOfSculptMonthProduct, loading:bestOfSculptMonthLoading} = useWorkout(
      5354,
      null,
      null,
      workoutsConfig
  );

  const {data:cardioGlowUp4WeeksProduct, loading:cardioGlowUp4WeeksLoading} = useWorkout(
      5342,
      null,
      null,
      workoutsConfig
  );


  const {data:feelGoodFactory4WeeksProduct, loading:feelGoodFactory4WeeksLoading} = useWorkout(
      5137,
      null,
      null,
      workoutsConfig
  );

  const {data:twentyOneVol24WeeksProduct, loading:twentyOneVol24WeeksLoading} = useWorkout(
      4991,
      null,
      null,
      workoutsConfig
  );

  const {data:greensAndGlow4WeeksProduct, loading:greensAndGlow4WeeksLoading} = useWorkout(
      4880,
      null,
      null,
      workoutsConfig
  );

  const {data:vacayGuide4WeeksProduct, loading:vacayGuide4WeeksLoading} = useWorkout(
      4846,
      null,
      null,
      workoutsConfig
  );

  const {data:feelGoodSummer4WeeksProduct, loading:feelGoodSummer4WeeksLoading} = useWorkout(
      4677,
      null,
      null,
      workoutsConfig
  );

  const {data:leanAndStrongTransformationVol24WeeksProduct, loading:leanAndStrongTransformationVol24WeeksLoading} = useWorkout(
      4540,
      null,
      null,
      workoutsConfig
  );

  const {data:leanAndStrongTransformationVol14WeeksProduct, loading:leanAndStrongTransformationVol14WeeksLoading} = useWorkout(
      4535,
      null,
      null,
      workoutsConfig
  );

  const {data:busyGirlBlackFriday4WeeksProduct, loading:busyGirlBlackFriday4WeeksLoading} = useWorkout(
      5341,
      null,
      null,
      workoutsConfig
  );

  const {data:recoverAndStrengthenPilatesProduct, loading:recoverAndStrengthenPilatesLoading} = useWorkout(
      5629,
      null,
      null,
      workoutsConfig
  );

  const {data:winterGlowUpProduct, loading:winterGlowUpLoading} = useWorkout(
      5681,
      null,
      null,
      workoutsConfig
  );

  const {data:lowerBodyProduct, loading:lowerBodyLoading} = useWorkout(
      5694,
      null,
      null,
      workoutsConfig
  );

  const {data:upperBodyAbsAndShredProduct, loading:upperBodyAbsAndShredLoading} = useWorkout(
      5713,
      null,
      null,
      workoutsConfig
  );

  const {data:sculptBurnAndShapeProduct, loading:sculptBurnAndShapeLoading} = useWorkout(
      5739,
      null,
      null,
      workoutsConfig
  );

  const {data: ohHiitProduct, loading: ohHiitLoading} = useWorkout(
      5766,
      null,
      null,
      workoutsConfig
  )

  const {data: hourglassSculptAndStrengthenProduct, loading: hourglassSculptAndStrengthenLoading} = useWorkout(
      5784,
      null,
      null,
      workoutsConfig
  )

  const {data: ultimateWeekOfBattlesProduct, loading: ultimateWeekOfBattlesLoading} = useWorkout(
      5811,
      null,
      null,
      workoutsConfig
  )

  const {data: powerPilatesProduct, loading: powerPilatesLoading} = useWorkout(
      5819,
      null,
      null,
      workoutsConfig,
  )

  const {data: leanSummerMethodPhase1Product, loading: leanSummerMethodPhase1Loading} = useWorkout(
      5885,
      null,
      null,
      workoutsConfig,
  )

  const {data: leanSummerMethodPhase2Product, loading: leanSummerMethodPhase2Loading} = useWorkout(
      5985,
      null,
      null,
      workoutsConfig,
  )


  const {data: leanSecondBirthdayBashProduct, loading: leanSecondBirthdayBashLoading} = useWorkout(
      6044,
      null,
      null,
      workoutsConfig,
  )

  const {data: twentyOneVol3Product, loading: twentyOneVol3Loading} = useWorkout(
      6157,
      null,
      null,
      workoutsConfig,
  )


  const {data: fatBurnCoreGaloreProduct, loading: fatBurnCoreGaloreLoading} = useWorkout(
      6177,
      null,
      null,
      workoutsConfig,
  )

  const {data: legsSculptFatBurnProduct, loading: legsSculptFatBurnLoading} = useWorkout(
      6273,
      null,
      null,
      workoutsConfig,
  )

  const {data: yourStrongestSelfProduct, loading: yourStrongestSelfLoading} = useWorkout(
      6292,
      null,
      null,
      workoutsConfig,
  )

  const {data: shapeBurnPilatesProduct, loading: shapeBurnPilatesLoading} = useWorkout(
      6301,
      null,
      null,
      workoutsConfig,
  )

  const {data: shredWithWeightsProduct, loading: shredWithWeightsLoading} = useWorkout(
      6341,
      null,
      null,
      workoutsConfig,
  )

  const {data: hourGlassShredShapeProduct, loading: hourGlassShredShapeLoading} = useWorkout(
      6358,
      null,
      null,
      workoutsConfig,
  )

  const {data: absAndUpperBodyProduct, loading: absAndUpperBodyLoading} = useWorkout(
      6367,
      null,
      null,
      workoutsConfig,
  )

  const {data: leanEmpoweredMethodPhase1Product, loading: leanEmpoweredMethodPhase1Loading} = useWorkout(
      6368,
      null,
      null,
      workoutsConfig,
  )


  const {data: leanEmpoweredMethodPhase2Product, loading: leanEmpoweredMethodPhase2Loading} = useWorkout(
      6613,
      null,
      null,
      workoutsConfig,
  )


  const {data: halloleanHellWeekProduct, loading: halloleanHellWeekLoading} = useWorkout(
      6623,
      null,
      null,
      workoutsConfig,
  )

  const {data: pilatesBootcampChallengeProduct, loading: pilatesBootcampChallengeLoading} = useWorkout(
      6804,
      null,
      null,
      workoutsConfig,
  )

  const {data: twentyFiveDaysOfLEANMassProduct, loading: twentyFiveDaysOfLEANMassLoading} = useWorkout(
      6831,
      null,
      null,
      workoutsConfig,
  )

  const {data: setYourNewYearGoalsProduct, loading: setYourNewYearGoalsLoading} = useWorkout(
    6877,
    null,
    null,
    workoutsConfig,
  )

  const {data: newYearPrepProduct, loading: newYearPrepLoading} = useWorkout(
    6883,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionPhase1Product, loading: pilatesEvolutionPhase1Loading} = useWorkout(
    7120,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek1Product, loading: pilatesEvolutionWeek1Loading} = useWorkout(
    6920,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek2Product, loading: pilatesEvolutionWeek2Loading} = useWorkout(
    7014,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek3Product, loading: pilatesEvolutionWeek3Loading} = useWorkout(
    7125,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek4Product, loading: pilatesEvolutionWeek4Loading} = useWorkout(
    7147,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionPhase2Product, loading: pilatesEvolutionPhase2Loading} = useWorkout(
    7209,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek5Product, loading: pilatesEvolutionWeek5Loading} = useWorkout(
    7168,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek6Product, loading: pilatesEvolutionWeek6Loading} = useWorkout(
    7185,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesEvolutionWeek7Product, loading: pilatesEvolutionWeek7Loading} = useWorkout(
    7190,
    null,
    null,
    workoutsConfig,
  )
  const {data: pilatesEvolutionDay50Product, loading: pilatesEvolutionDay50Loading} = useWorkout(
      7198,
      null,
      null,
      workoutsConfig,
  )

  const {data: pilatesEvolutionWeek9Product, loading: pilatesEvolutionWeek9Loading} = useWorkout(
    7203,
    null,
    null,
    workoutsConfig,
  )

  const {data: bestOfLmpeProduct, loading: bestOfLmpeLoading} = useWorkout(
    7274,
    null,
    null,
    workoutsConfig,
  )

  const {data: weekOfWellnessProduct, loading: weekOfWellnessLoading} = useWorkout(
    7268,
    null,
    null,
    workoutsConfig,
  )

  const {data: matInspiredReformerPilatesProduct, loading: matInspiredReformerPilatesLoading} = useWorkout(
    7279,
    null,
    null,
    workoutsConfig,
  )

  const {data: ultimateStrengthChallengeProduct, loading: ultimateStrengthChallengeLoading} = useWorkout(
    7300,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesStrengthFusionProduct, loading: pilatesStrengthFusionLoading} = useWorkout(
    7349,
    null,
    null,
    workoutsConfig,
  )

  const {data: lowImpactFatBurnProduct, loading: lowImpactFatBurnLoading} = useWorkout(
    7427,
    null,
    null,
    workoutsConfig,
  )

  const {data: sunsOutBunsOutProduct, loading: sunsOutBunsOutLoading} = useWorkout(
    7437,
    null,
    null,
    workoutsConfig,
  )

  const {data: powerUpPilatesWeek3Product, loading: powerUpPilatesWeek3Loading} = useWorkout(
    7515,
    null,
    null,
    workoutsConfig,
  )

  const {data: summerSurprisePrepProduct, loading: summerSurprisePrepLoading} = useWorkout(
    7534,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesSummerSculptPhase1Product, loading: pilatesSummerSculptPhase1Loading} = useWorkout(
    7550,
    null,
    null,
    workoutsConfig,
  )

  const {data: pilatesSummerSculptPhase2Product, loading: pilatesSummerSculptPhase2Loading} = useWorkout(
    7645,
    null,
    null,
    workoutsConfig,
  )

  const {data: leanLifestyleProduct, loading: leanLifestyleLoading} = useWorkout(
    7736,
    null,
    null,
    workoutsConfig,
)

const {data: coreStrengthStabilityProduct, loading: coreStrengthStabilityLoading} = useWorkout(
  7766,
  null,
  null,
  workoutsConfig,
)

const {data: buildStrengthMonthWeek4Product, loading: buildStrengthMonthWeek4Loading} = useWorkout(
  7876,
  null,
  null,
  workoutsConfig,
)

const {data: resetAndThriveChallengeProduct, loading: resetAndThriveChallengeLoading} = useWorkout(
  7898,
  null,
  null,
  workoutsConfig,
)

const {data: buildYourStengthMonthProduct, loading: buildYourStengthMonthLoading} = useWorkout(
  7919,
  null,
  null,
  workoutsConfig,
)

const {data: bestOfLeanMethodProduct, loading: bestOfLeanMethodLoading} = useWorkout(
  7902,
  null,
  null,
  workoutsConfig,
)

const {data: ultimateStrengthChallenge2Product, loading: ultimateStrengthChallenge2Loading} = useWorkout(
  7998,
  null,
  null,
  workoutsConfig,
)

const {data: halloLeanHellWeek24Product, loading: halloLeanHellWeek24Loading} = useWorkout(
  8019,
  null,
  null,
  workoutsConfig,
)

const {data: twentyOneVol4Product, loading: twentyOneVol4Loading} = useWorkout(
  8104,
  null,
  null,
  workoutsConfig,
)

const {data: bestOfSculptWeek1Product, loading: bestOfSculptWeek1Loading} = useWorkout(
  8035,
  null,
  null,
  workoutsConfig,
)

const {data: bestOfSculptStrengthProduct, loading: bestOfSculptStrengthLoading} = useWorkout(
  8193,
  null,
  null,
  workoutsConfig,
)

const {data: winterGlowUp2Product, loading: winterGlowUp2Loading} = useWorkout(
  8209,
  null,
  null,
  workoutsConfig,
)

const {data: newYearGoalsProduct, loading: newYearGoalsLoading} = useWorkout(
  8227,
  null,
  null,
  workoutsConfig,
)

const {data: levelUpPrepProduct, loading: levelUpPrepLoading} = useWorkout(
  8277,
  null,
  null,
  workoutsConfig,
)

const {data: leanMethodLevelUpProduct, loading: leanMethodLevelUpLoading} = useWorkout(
  8292,
  null,
  null,
  workoutsConfig,
)

const {data: leanMethodDeloadWeekProduct, loading: leanMethodDeloadWeekLoading} = useWorkout(
  8651,
  null,
  null,
  workoutsConfig,
)

const {data: toneAndTransformProduct, loading: toneAndTransformLoading} = useWorkout(
  8745,
  null,
  null,
  workoutsConfig,
)

const {data: coreStrengthStability2Product, loading: coreStrengthStability2Loading} = useWorkout(
  8750,
  null,
  null,
  workoutsConfig,
)

const {data: sevenDayBootySculptProduct, loading: sevenDayBootySculptLoading} = useWorkout(
  8787,
  null,
  null,
  workoutsConfig,
)

const {data: defineAndRefineProduct, loading: defineAndRefineLoading} = useWorkout(
  8792,
  null,
  null,
  workoutsConfig,
)

const {data: sweatyBootcampProduct, loading: sweatyBootcampLoading} = useWorkout(
  8904,
  null,
  null,
  workoutsConfig,
)

const {data: summerPrep2025Product, loading: summerPrep2025Loading} = useWorkout(
  8949,
  null,
  null,
  workoutsConfig,
)

const {data: leanMethodElevateP1Product, loading: leanMethodElevateP1Loading} = useWorkout(
  8982,
  null,
  null,
  workoutsConfig,
)

const {data: leamMethodLevel5Product, loading: leamMethodLevel5Loading} = useWorkout(
  9034,
  null,
  null,
  workoutsConfig,
)

const {data: leamMethodLevel4Product, loading: leamMethodLevel4Loading} = useWorkout(
  9035,
  null,
  null,
  workoutsConfig,
)

const {data: leamMethodLevel3Product, loading: leamMethodLevel3Loading} = useWorkout(
  9028,
  null,
  null,
  workoutsConfig,
)

const {data: leamMethodLevel2Product, loading: leamMethodLevel2Loading} = useWorkout(
  9023,
  null,
  null,
  workoutsConfig,
)

const {data: leanMethodElevateP2Product, loading: leanMethodElevateP2Loading} = useWorkout(
  8986,
  null,
  null,
  workoutsConfig,
)

const {data: pilatesLowImpactWeekProduct, loading: pilatesLowImpactWeekLoading} = useWorkout(
  9197,
  null,
  null,
  workoutsConfig,
)

const {data: scultLeanMuscleProduct, loading: scultLeanMuscleLoading} = useWorkout(
  9202,
  null,
  null,
  workoutsConfig,
)

const {data: buildStrengthPilatesProduct, loading: buildStrengthPilatesLoading} = useWorkout(
  9209,
  null,
  null,
  workoutsConfig,
)

const {data: leanAbsAndUpperBodyProduct, loading: leanAbsAndUpperBodyLoading} = useWorkout(
  9260,
  null,
  null,
  workoutsConfig,
)

const {data: weightsAndBurnProduct, loading: weightsAndBurnLoading} = useWorkout(
  9265,
  null,
  null,
  workoutsConfig,
)

const {data: resetChallengeProduct, loading: resetChallengeLoading} = useWorkout(
  9307,
  null,
  null,
  workoutsConfig,
)

const {data: powerLegsAndSculptProduct, loading: powerLegsAndSculptLoading} = useWorkout(
  9350,
  null,
  null,
  workoutsConfig,
)

const {data: wellnessReadyWeekProduct, loading: wellnessReadyWeekLoading} = useWorkout(
  9375,
  null,
  null,
  workoutsConfig,
)

const {data: pilatedFusionBurnProduct, loading: pilatedFusionBurnLoading} = useWorkout(
  9360,
  null,
  null,
  workoutsConfig,
)

const {data: leanSculptAndFlowProduct, loading: leanSculptAndFlowLoading} = useWorkout(
  9417,
  null,
  null,
  workoutsConfig,
)

const {data: strengthAndBalanceProduct, loading: strengthAndBalanceLoading} = useWorkout(
  9450,
  null,
  null,
  workoutsConfig,
)

const {data: transformationPrepProduct, loading: transformationPrepLoading} = useWorkout(
  9462,
  null,
  null,
  workoutsConfig,
)

const {data: leanMethodTransformationProduct, loading: leanMethodTransformationLoading} = useWorkout(
  9479,
  null,
  null,
  workoutsConfig,
)

const {data: hourglassSculptProduct, loading: hourglassSculptLoading} = useWorkout(
  9585,
  null,
  null,
  workoutsConfig,
)

const {data: lowerBodyStrengthSculptProduct, loading: lowerBodyStrengthSculptLoading} = useWorkout(
  9633,
  null,
  null,
  workoutsConfig,
)

const {data: reformerInspiredPilatesProduct, loading: reformerInspiredPilatesLoading} = useWorkout(
  9650,
  null,
  null,
  workoutsConfig,
)

const {data: coreCardioStengthProduct, loading: coreCardioStengthLoading} = useWorkout(
  9654,
  null,
  null,
  workoutsConfig,
)

const {data: defineChallengePhaseProduct, loading: defineChallengePhaseLoading} = useWorkout(
  9684,
  null,
  null,
  workoutsConfig,
)

const {data: defineChallengePhase2Product, loading: defineChallengePhas2eLoading} = useWorkout(
  9821,
  null,
  null,
  workoutsConfig,
)

const {data: lean2in1PrepWeekProduct, loading: lean2in1PrepWeekLoading} = useWorkout(
  9906,
  null,
  null,
  workoutsConfig,
)

const {data: lean2In1MethodPilatesXStrengthFullGuideProduct, loading: lean2In1MethodPilatesXStrengthFullGuideLoading} = useWorkout(
  9905,
  null,
  null,
  workoutsConfig,
)

const {data: lean7DayBootySculpt2026Product, loading: lean7DayBootySculpt2026Loading} = useWorkout(
  9974,
  null,
  null,
  workoutsConfig,
)

const {data: leanCoreStrengthAndStabilityProduct, loading: leanCoreStrengthAndStabilityLoading} = useWorkout(
  9996,
  null,
  null,
  workoutsConfig,
)

const {data: leanUltimateStrengthWeekProduct, loading: leanUltimateStrengthWeekLoading} = useWorkout(
  10015,
  null,
  null,
  workoutsConfig,
)

const {data: leanAbsAndUpperBody2026Product, loading: leanAbsAndUpperBody2026Loading} = useWorkout(
  10021,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSculptChallengePrepWeekProduct, loading: leanSummerSculptChallengePrepWeekLoading} = useWorkout(
  10045,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSculptChallengeFullGuideProduct, loading: leanSummerSculptChallengeFullGuideLoading} = useWorkout(
  10050,
  null,
  null,
  workoutsConfig,
)

const {data: leanLowerBodyStrengthandCardioWeekProduct, loading: leanLowerBodyStrengthandCardioWeekLoading} = useWorkout(
  10181,
  null,
  null,
  workoutsConfig,
)

// const {data: lean2In1AbsAndSculptFullGuideProduct, loading: lean2In1AbsAndSculptFullGuideLoading} = useWorkout(
//   10191,
//   null,
//   null,
//   workoutsConfig,
// )

const {data: lean2In1AbsAndSculptWeek1Product, loading: lean2In1AbsAndSculptWeek1Loading} = useWorkout(
  10192,
  null,
  null,
  workoutsConfig,
)

const {data: lean2in1AbsAndSculptFullLengthGuideProduct, loading: lean2in1AbsAndSculptFullLengthGuideLoading} = useWorkout(
  10191,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSeriesArmsProduct, loading: leanSummerSeriesArmsLoading} = useWorkout(
  10269,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSeriesLowerBodyProduct, loading: leanSummerSeriesLowerBodyLoading} = useWorkout(
  10286,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSeriesQuickBurnProduct, loading: leanSummerSeriesQuickBurnLoading} = useWorkout(
  10291,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSeriesAbsProduct, loading: leanSummerSeriesAbsLoading} = useWorkout(
  10296,
  null,
  null,
  workoutsConfig,
)

const {data: leanSummerSeriesGlutes2Product, loading: leanSummerSeriesGlutes2Loading} = useWorkout(
  10311,
  null,
  null,
  workoutsConfig,
)

const {data: summerSeriesUpperBodyProduct, loading: summerSeriesUpperBodyLoading} = useWorkout(
  10316,
  null,
  null,
  workoutsConfig,
)

const {data: leanChallengePrepWeekGuideProduct, loading: leanChallengePrepWeekGuideLoading} = useWorkout(
  10354,
  null,
  null,
  workoutsConfig,
)

const {data: reDefineBeginnerProduct, loading: reDefineBeginnerLoading} = useWorkout(
  10335,
  null,
  null,
  workoutsConfig,
)

const {data: redefineIntermediateandAdvancedProduct, loading: redefineIntermediateandAdvancedLoading} = useWorkout(
  10349,
  null,
  null,
  workoutsConfig,
)

const {data: redefineIntermediateandAdvancedFullLengthProduct, loading: redefineIntermediateandAdvancedFullLengthLoading} = useWorkout(
  10334,
  null,
  null,
  workoutsConfig,
)

const {data: redefineIntermediateWeek2Product, loading: redefineIntermediateWeek2Loading} = useWorkout(
  10380,
  null,
  null,
  workoutsConfig,
)

const {data: redefineWeek3Product, loading: redefineWeek3Loading} = useWorkout(
  10385,
  null,
  null,
  workoutsConfig,
)

const {data: redefineWeek4Product, loading: redefineWeek4Loading} = useWorkout(
  10398,
  null,
  null,
  workoutsConfig,
)

const {data: redefineWeek5Product, loading: redefineWeek5Loading} = useWorkout(
  10404,
  null,
  null,
  workoutsConfig,
)

const {data: redefineWeek6Product, loading: redefineWeek6Loading} = useWorkout(
  10409,
  null,
  null,
  workoutsConfig,
)

  const loading =
      redefineWeek6Loading ||
      redefineWeek5Loading ||
      redefineWeek4Loading ||
      redefineWeek3Loading ||
      redefineIntermediateWeek2Loading ||
      redefineIntermediateandAdvancedFullLengthLoading ||
      redefineIntermediateandAdvancedLoading ||
      reDefineBeginnerLoading ||
      leanChallengePrepWeekGuideLoading ||
      summerSeriesUpperBodyLoading ||
      leanSummerSeriesGlutes2Loading ||
      leanSummerSeriesAbsLoading ||
      leanSummerSeriesQuickBurnLoading ||
      leanSummerSeriesLowerBodyLoading ||
      leanSummerSeriesArmsLoading ||
      lean2in1AbsAndSculptFullLengthGuideLoading ||
      lean2In1AbsAndSculptWeek1Loading ||
      // lean2In1AbsAndSculptFullGuideLoading ||
      leanLowerBodyStrengthandCardioWeekLoading ||
      leanSummerSculptChallengeFullGuideLoading ||
      leanSummerSculptChallengePrepWeekLoading ||
      leanAbsAndUpperBody2026Loading ||
      leanUltimateStrengthWeekLoading ||
      leanCoreStrengthAndStabilityLoading ||
      lean7DayBootySculpt2026Loading ||
      lean2In1MethodPilatesXStrengthFullGuideLoading ||
      lean2in1PrepWeekLoading ||
      defineChallengePhas2eLoading ||
      defineChallengePhaseLoading ||
      coreCardioStengthLoading ||
      reformerInspiredPilatesLoading ||
      lowerBodyStrengthSculptLoading ||
      hourglassSculptLoading ||
      leanMethodTransformationLoading ||
      transformationPrepLoading ||
      strengthAndBalanceLoading ||
      leanSculptAndFlowLoading ||
      pilatedFusionBurnLoading ||
      wellnessReadyWeekLoading ||
      powerLegsAndSculptLoading ||
      resetChallengeLoading ||
      weightsAndBurnLoading ||
      leanAbsAndUpperBodyLoading ||
      buildStrengthPilatesLoading ||
      scultLeanMuscleLoading ||
      pilatesLowImpactWeekLoading ||
      leanMethodElevateP2Loading ||
      leamMethodLevel5Loading ||
      leamMethodLevel4Loading ||
      leamMethodLevel3Loading ||
      leamMethodLevel2Loading ||
      leanMethodElevateP1Loading ||
      summerPrep2025Loading ||
      sweatyBootcampLoading ||
      defineAndRefineLoading ||
      sevenDayBootySculptLoading ||
      coreStrengthStability2Loading ||
      toneAndTransformLoading ||
      leanMethodDeloadWeekLoading ||
      leanMethodLevelUpLoading ||
      levelUpPrepLoading ||
      newYearGoalsLoading ||
      winterGlowUp2Loading ||
      bestOfSculptStrengthLoading ||
      bestOfSculptWeek1Loading ||
      twentyOneVol4Loading ||
      halloLeanHellWeek24Loading ||
      ultimateStrengthChallenge2Loading ||
      buildYourStengthMonthLoading ||
      bestOfLeanMethodLoading ||
      resetAndThriveChallengeLoading ||
      buildStrengthMonthWeek4Loading ||
      coreStrengthStabilityLoading ||
      leanLifestyleLoading ||
      pilatesSummerSculptPhase2Loading ||
      pilatesSummerSculptPhase1Loading ||
      powerUpPilatesWeek3Loading ||
      summerSurprisePrepLoading ||
      sunsOutBunsOutLoading ||
      lowImpactFatBurnLoading ||
      pilatesStrengthFusionLoading ||
      ultimateStrengthChallengeLoading ||
      matInspiredReformerPilatesLoading ||
      weekOfWellnessLoading ||
      bestOfLmpeLoading ||
      pilatesEvolutionWeek9Loading ||
      pilatesEvolutionDay50Loading ||
      pilatesEvolutionWeek7Loading ||
      pilatesEvolutionWeek6Loading ||
      pilatesEvolutionWeek5Loading ||
      pilatesEvolutionPhase2Loading ||
      pilatesEvolutionWeek4Loading ||
      pilatesEvolutionWeek3Loading ||
      pilatesEvolutionWeek2Loading ||
      pilatesEvolutionWeek1Loading ||
      pilatesEvolutionPhase1Loading ||
      newYearPrepLoading ||
      setYourNewYearGoalsLoading ||
      twentyFiveDaysOfLEANMassLoading ||
      pilatesBootcampChallengeLoading ||
      halloleanHellWeekLoading ||
      leanEmpoweredMethodPhase2Loading ||
      leanEmpoweredMethodPhase1Loading ||
      absAndUpperBodyLoading ||
      hourGlassShredShapeLoading ||
      shredWithWeightsLoading ||
      shapeBurnPilatesLoading ||
      yourStrongestSelfLoading ||
      legsSculptFatBurnLoading ||
      fatBurnCoreGaloreLoading ||
      twentyOneVol3Loading ||
      leanSecondBirthdayBashLoading ||
      leanSummerMethodPhase2Loading ||
  leanSummerMethodPhase1Loading ||
    powerPilatesLoading ||
    ultimateWeekOfBattlesLoading ||
      hourglassSculptAndStrengthenLoading ||
    ohHiitLoading||
    sculptBurnAndShapeLoading ||
    upperBodyAbsAndShredLoading ||
    lowerBodyLoading ||
    winterGlowUpLoading ||
    recoverAndStrengthenPilatesLoading ||
    feelGoodFactory4WeeksLoading ||
    twentyOneVol24WeeksLoading ||
    greensAndGlow4WeeksLoading ||
    vacayGuide4WeeksLoading ||
    feelGoodSummer4WeeksLoading ||
    leanAndStrongTransformationVol24WeeksLoading ||
    leanAndStrongTransformationVol14WeeksLoading ||
    cardioGlowUp4WeeksLoading ||
    bestOfSculptMonthLoading ||
    leanMass2022RepackedLoading ||
    leanTransformationMethodPhase1Loading ||
    leanTransformationMethodPhase2Loading ||
      leanAndPowerfulLoading ||
      cardioGlowUpLoading ||
      busyGirlBlackFriday4WeeksLoading ||
      pilatesBodyLoading ||
    bestOfStrengthLoading ||
    halloLeanHellWeek22Loading ||
    halloLeanPrepLoading ||
    gimmeGummyMoreLoading ||
    birthdayLoading ||
    bestOfShredLoading ||
    bestOfSculptLoading ||
    periodFriendlyLoading ||
    // maryBraunWeek4Loading ||
    // maryBraunWeek3Loading ||
    preSummerBootcampLoading ||
    deloadPilatesBootCampLoading ||
    // maryBraunWeek2Loading ||
    // maryBraunWeek1Loading ||
    turnItUpLoading ||
    galentineLoading ||
    week4Loading ||
    pilatesProductLoading ||
    leanMasProductLoading ||
    strongCapableProductLoading ||
    cutStudentProductLoading ||
    halloLeanProductLoading ||
    twentyOneProductLoading ||
    smallerWaistWorkoutProductLoading ||
    hourglassWorkoutProductLoading ||
    bootyWorkoutProductLoading ||
    sabriWorkoutProductLoading ||
    howToBandsDataLoading ||
    nutritionLoading ||
    learnExercisesDataLoading ||
    physioDataLoading ||
    profileUserDataLoading ||
    currentGuideProductLoading ||
    challengesDataLoading;

  /*
   * "Classes" is an standardized abstract concept so that we can consistently
   * render workout and course classes as a single class component for the user
   * */

  /*
        TODO: This does not  currently work as completed sessions are now saved in user data
     */
  // const {
  //     loading: completedSessionsLoading,
  //     data: completedSessions,
  //     totalCompletedSessions
  // } = useCompletedSessions();

  // const isCompletedSession = (classItem) => {
  //     const completedSessionIDs = completedSessions?.map(({session_id}) => parseInt(session_id));
  //     return completedSessionIDs.indexOf(parseInt(classItem?.sessionId)) > -1
  // }

  // Hide certain challenges based on their releaseTimestamp metadata key
  challengesData.sections = challengesData.sections?.map((section) => ({
    ...section,
    lessons: section?.lessons.filter(({ assets }) => {
      const releaseTimestamp = get(JSON.parse(get(assets, "[0].meta_data", {})), "releaseTimestamp");
      return releaseTimestamp && moment().diff(moment(releaseTimestamp)) > 0;
    }),
  }));

  const classes = [
    ...((guideProduct &&
      transformWorkoutProductToClasses(guideProduct, {
        isCurrentGuide: true,
      })) ||
      []),
    ...transformWorkoutProductToClasses(redefineWeek6Product),
    ...transformWorkoutProductToClasses(redefineWeek5Product),
    ...transformWorkoutProductToClasses(redefineWeek4Product),
    ...transformWorkoutProductToClasses(redefineWeek3Product),
    ...transformWorkoutProductToClasses(redefineIntermediateWeek2Product),
    ...transformWorkoutProductToClasses(redefineIntermediateandAdvancedFullLengthProduct),
    ...transformWorkoutProductToClasses(redefineIntermediateandAdvancedProduct),
    ...transformWorkoutProductToClasses(reDefineBeginnerProduct),
    ...transformWorkoutProductToClasses(leanChallengePrepWeekGuideProduct),
    ...transformWorkoutProductToClasses(summerSeriesUpperBodyProduct),
    ...transformWorkoutProductToClasses(leanSummerSeriesGlutes2Product),
    ...transformWorkoutProductToClasses(leanSummerSeriesAbsProduct),
    ...transformWorkoutProductToClasses(leanSummerSeriesQuickBurnProduct),
    ...transformWorkoutProductToClasses(leanSummerSeriesLowerBodyProduct),
    ...transformWorkoutProductToClasses(leanSummerSeriesArmsProduct),
    ...transformWorkoutProductToClasses(lean2in1AbsAndSculptFullLengthGuideProduct),
    ...transformWorkoutProductToClasses(lean2In1AbsAndSculptWeek1Product),
    // ...transformWorkoutProductToClasses(lean2In1AbsAndSculptFullGuideProduct),
    ...transformWorkoutProductToClasses(leanLowerBodyStrengthandCardioWeekProduct),
    ...transformWorkoutProductToClasses(leanSummerSculptChallengeFullGuideProduct),
    ...transformWorkoutProductToClasses(leanSummerSculptChallengePrepWeekProduct),
    ...transformWorkoutProductToClasses(leanAbsAndUpperBody2026Product),
    ...transformWorkoutProductToClasses(leanUltimateStrengthWeekProduct),
    ...transformWorkoutProductToClasses(leanCoreStrengthAndStabilityProduct),
    ...transformWorkoutProductToClasses(lean7DayBootySculpt2026Product),
    ...transformWorkoutProductToClasses(lean2In1MethodPilatesXStrengthFullGuideProduct),
    ...transformWorkoutProductToClasses(lean2in1PrepWeekProduct),
    ...transformWorkoutProductToClasses(defineChallengePhase2Product),
    ...transformWorkoutProductToClasses(defineChallengePhaseProduct),
    ...transformWorkoutProductToClasses(coreCardioStengthProduct),
    ...transformWorkoutProductToClasses(reformerInspiredPilatesProduct),
    ...transformWorkoutProductToClasses(lowerBodyStrengthSculptProduct),
    ...transformWorkoutProductToClasses(hourglassSculptProduct),
    ...transformWorkoutProductToClasses(leanMethodTransformationProduct),
    ...transformWorkoutProductToClasses(transformationPrepProduct),
    ...transformWorkoutProductToClasses(strengthAndBalanceProduct),
    ...transformWorkoutProductToClasses(pilatedFusionBurnProduct),
    ...transformWorkoutProductToClasses(leanSculptAndFlowProduct),
    ...transformWorkoutProductToClasses(wellnessReadyWeekProduct),
    ...transformWorkoutProductToClasses(powerLegsAndSculptProduct),
    ...transformWorkoutProductToClasses(resetChallengeProduct),
    ...transformWorkoutProductToClasses(weightsAndBurnProduct),
    ...transformWorkoutProductToClasses(leanAbsAndUpperBodyProduct),
    ...transformWorkoutProductToClasses(buildStrengthPilatesProduct),
    ...transformWorkoutProductToClasses(scultLeanMuscleProduct),
    ...transformWorkoutProductToClasses(pilatesLowImpactWeekProduct),
    ...transformWorkoutProductToClasses(leanMethodElevateP2Product),
    ...transformWorkoutProductToClasses(leanMethodElevateP1Product),
    ...transformWorkoutProductToClasses(summerPrep2025Product),
    ...transformWorkoutProductToClasses(sweatyBootcampProduct),
    ...transformWorkoutProductToClasses(defineAndRefineProduct),
    ...transformWorkoutProductToClasses(sevenDayBootySculptProduct),
    ...transformWorkoutProductToClasses(coreStrengthStability2Product),
    ...transformWorkoutProductToClasses(toneAndTransformProduct),
    ...transformWorkoutProductToClasses(leanMethodDeloadWeekProduct),
    ...transformWorkoutProductToClasses(leanMethodLevelUpProduct),
    ...transformWorkoutProductToClasses(levelUpPrepProduct),
    ...transformWorkoutProductToClasses(newYearGoalsProduct),
    ...transformWorkoutProductToClasses(winterGlowUp2Product),
    ...transformWorkoutProductToClasses(bestOfSculptStrengthProduct),
    ...transformWorkoutProductToClasses(bestOfSculptWeek1Product),
    ...transformWorkoutProductToClasses(twentyOneVol4Product),
    ...transformWorkoutProductToClasses(halloLeanHellWeek24Product),
    ...transformWorkoutProductToClasses(ultimateStrengthChallenge2Product),
    ...transformWorkoutProductToClasses(buildYourStengthMonthProduct),
    ...transformWorkoutProductToClasses(bestOfLeanMethodProduct),
    ...transformWorkoutProductToClasses(resetAndThriveChallengeProduct),
    ...transformWorkoutProductToClasses(buildStrengthMonthWeek4Product),
    ...transformWorkoutProductToClasses(coreStrengthStabilityProduct),
    ...transformWorkoutProductToClasses(leanLifestyleProduct),
    ...transformWorkoutProductToClasses(pilatesSummerSculptPhase2Product),
    ...transformWorkoutProductToClasses(pilatesSummerSculptPhase1Product),
    ...transformWorkoutProductToClasses(powerUpPilatesWeek3Product),
    ...transformWorkoutProductToClasses(summerSurprisePrepProduct),
    ...transformWorkoutProductToClasses(sunsOutBunsOutProduct),
    ...transformWorkoutProductToClasses(lowImpactFatBurnProduct),
    ...transformWorkoutProductToClasses(pilatesStrengthFusionProduct),
    ...transformWorkoutProductToClasses(ultimateStrengthChallengeProduct),
    ...transformWorkoutProductToClasses(matInspiredReformerPilatesProduct),
    ...transformWorkoutProductToClasses(weekOfWellnessProduct),
    ...transformWorkoutProductToClasses(bestOfLmpeProduct),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek9Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionDay50Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek7Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek6Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek5Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionPhase2Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek4Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek3Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek2Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionWeek1Product),
    ...transformWorkoutProductToClasses(pilatesEvolutionPhase1Product),
    ...transformWorkoutProductToClasses(newYearPrepProduct),
    ...transformWorkoutProductToClasses(setYourNewYearGoalsProduct),
    ...transformWorkoutProductToClasses(twentyFiveDaysOfLEANMassProduct),
    ...transformWorkoutProductToClasses(pilatesBootcampChallengeProduct),
    ...transformWorkoutProductToClasses(halloleanHellWeekProduct),
    ...transformWorkoutProductToClasses(leanEmpoweredMethodPhase2Product),
    ...transformWorkoutProductToClasses(leanEmpoweredMethodPhase1Product),
    ...transformWorkoutProductToClasses(absAndUpperBodyProduct),
    ...transformWorkoutProductToClasses(hourGlassShredShapeProduct),
    ...transformWorkoutProductToClasses(shredWithWeightsProduct),
    ...transformWorkoutProductToClasses(shapeBurnPilatesProduct),
    ...transformWorkoutProductToClasses(yourStrongestSelfProduct),
    ...transformWorkoutProductToClasses(legsSculptFatBurnProduct),
    ...transformWorkoutProductToClasses(fatBurnCoreGaloreProduct),
    ...transformWorkoutProductToClasses(twentyOneVol3Product),
    ...transformWorkoutProductToClasses(leanSecondBirthdayBashProduct),
    ...transformWorkoutProductToClasses(leanSummerMethodPhase2Product),
    ...transformWorkoutProductToClasses(leanSummerMethodPhase1Product),
    ...transformWorkoutProductToClasses(powerPilatesProduct),
    ...transformWorkoutProductToClasses(ultimateWeekOfBattlesProduct),
    ...transformWorkoutProductToClasses(hourglassSculptAndStrengthenProduct),
    ...transformWorkoutProductToClasses(ohHiitProduct),
    ...transformWorkoutProductToClasses(sculptBurnAndShapeProduct),
    ...transformWorkoutProductToClasses(upperBodyAbsAndShredProduct),
    ...transformWorkoutProductToClasses(lowerBodyProduct),
    ...transformWorkoutProductToClasses(winterGlowUpProduct),
    ...transformWorkoutProductToClasses(recoverAndStrengthenPilatesProduct),
    ...transformWorkoutProductToClasses(leanTransformationMethodPhase1Product),
    ...transformWorkoutProductToClasses(leanTransformationMethodPhase2Product),
    ...transformWorkoutProductToClasses(bestOfSculptMonthProduct),
    ...transformWorkoutProductToClasses(leanMass2022RepackedProduct),
    ...transformWorkoutProductToClasses(leanAndPowerfulProduct),
    ...transformWorkoutProductToClasses(cardioGlowUp4WeeksProduct),
    ...transformWorkoutProductToClasses(cardioGlowUpProduct),
      ...transformWorkoutProductToClasses(busyGirlBlackFriday4WeeksProduct),
    ...transformWorkoutProductToClasses(pilatesBodyProduct),
    ...transformWorkoutProductToClasses(bestOfStrengthProduct),
    ...transformWorkoutProductToClasses(halloLeanHellWeek22Product),
    ...transformWorkoutProductToClasses(halloLeanPrepProduct),
    ...transformWorkoutProductToClasses(feelGoodFactory4WeeksProduct),
    ...transformWorkoutProductToClasses(gimmeGummyMoreProduct),
    ...transformWorkoutProductToClasses(twentyOneVol24WeeksProduct),
    ...transformWorkoutProductToClasses(greensAndGlow4WeeksProduct),
    ...transformWorkoutProductToClasses(vacayGuide4WeeksProduct),
    ...transformWorkoutProductToClasses(birthdayProduct),
    ...transformWorkoutProductToClasses(bestOfShredProduct),
    ...transformWorkoutProductToClasses(bestOfSculptProduct),
    ...transformWorkoutProductToClasses(feelGoodSummer4WeeksProduct),
    ...transformWorkoutProductToClasses(periodFriendlyProduct),
    // ...transformWorkoutProductToClasses(maryBraunWeek4Product),
    // ...transformWorkoutProductToClasses(maryBraunWeek3Product),
    ...transformWorkoutProductToClasses(preSummerBootcampProduct),
    ...transformWorkoutProductToClasses(deloadPilatesBootCampProduct),
    // ...transformWorkoutProductToClasses(maryBraunWeek2Product),
    // ...transformWorkoutProductToClasses(maryBraunWeek1Product),
    ...transformWorkoutProductToClasses(leanAndStrongTransformationVol24WeeksProduct),
    ...transformWorkoutProductToClasses(turnItUpProduct),
    ...transformWorkoutProductToClasses(leanAndStrongTransformationVol14WeeksProduct),
    ...transformWorkoutProductToClasses(galentineProduct),
    ...transformWorkoutProductToClasses(week4Product),
    ...transformWorkoutProductToClasses(pilatesWorkoutProduct),
    ...transformWorkoutProductToClasses(bootyWorkoutProduct),
    ...transformWorkoutProductToClasses(smallerWaistWorkoutProduct),
    ...transformWorkoutProductToClasses(hourglassWorkoutProduct),
    ...transformWorkoutProductToClasses(sabriWorkoutProduct),
    ...transformWorkoutProductToClasses(twentyOneWorkoutProduct),
    ...transformWorkoutProductToClasses(halloLeanWorkoutProduct),
    ...transformWorkoutProductToClasses(leanMasWorkoutProduct),
    ...transformWorkoutProductToClasses(cutStudentWorkoutProduct),
    ...transformWorkoutProductToClasses(strongCapableWorkoutProduct),
    ...transformCourseToClasses(learnExercisesData),
    ...transformCourseToClasses(howToBandsData),
    ...transformCourseToClasses(physioData),
    ...transformCourseToClasses(nutritionData),
    ...transformCourseToClasses(challengesData, {
      edits: {
        lockType: "challenge",
        lockId: (v, i) => !get(JSON.parse(get(v, "assets[0].meta_data", {})), "free"),
      },
    }),
  ];

  const guides = [
    redefineWeek6Product,
    redefineWeek5Product,
    redefineWeek4Product,
    redefineWeek3Product,
    redefineIntermediateWeek2Product,
    redefineIntermediateandAdvancedProduct,
    redefineIntermediateandAdvancedFullLengthProduct,
    reDefineBeginnerProduct,
    leanChallengePrepWeekGuideProduct,
    summerSeriesUpperBodyProduct,
    leanSummerSeriesGlutes2Product,
    leanSummerSeriesAbsProduct,
    leanSummerSeriesQuickBurnProduct,
    leanSummerSeriesLowerBodyProduct,
    leanSummerSeriesArmsProduct,
    lean2in1AbsAndSculptFullLengthGuideProduct,
    lean2In1AbsAndSculptWeek1Product,
    // lean2In1AbsAndSculptFullGuideProduct,
    leanLowerBodyStrengthandCardioWeekProduct,
    leanSummerSculptChallengeFullGuideProduct,
    leanSummerSculptChallengePrepWeekProduct,
    leanAbsAndUpperBody2026Product,
    leanUltimateStrengthWeekProduct,
    leanCoreStrengthAndStabilityProduct,
    lean7DayBootySculpt2026Product,
    lean2In1MethodPilatesXStrengthFullGuideProduct,
    lean2in1PrepWeekProduct,
    defineChallengePhase2Product,
    defineChallengePhaseProduct,
    coreCardioStengthProduct,
    reformerInspiredPilatesProduct,
    lowerBodyStrengthSculptProduct,
    hourglassSculptProduct,
    leanMethodTransformationProduct,
    transformationPrepProduct,
    strengthAndBalanceProduct,
    leanSculptAndFlowProduct,
    pilatedFusionBurnProduct,
    wellnessReadyWeekProduct,
    powerLegsAndSculptProduct,
    resetChallengeProduct,
    weightsAndBurnProduct,
    leanAbsAndUpperBodyProduct,
    buildStrengthPilatesProduct,
    scultLeanMuscleProduct,
    pilatesLowImpactWeekProduct,
    leanMethodElevateP2Product,
    leanMethodElevateP1Product,
    summerPrep2025Product,
    sweatyBootcampProduct,
    bestOfStrengthProduct,
    shapeBurnPilatesProduct,
    hourglassSculptAndStrengthenProduct,
    leanAndPowerfulProduct,
    defineAndRefineProduct,
    sevenDayBootySculptProduct,
    coreStrengthStability2Product,
    toneAndTransformProduct,
    leanMethodDeloadWeekProduct,
    leanMethodLevelUpProduct,
    levelUpPrepProduct,
    newYearGoalsProduct,
    winterGlowUp2Product,
    bestOfSculptStrengthProduct,
    bestOfSculptWeek1Product,
    twentyOneVol4Product,
    halloLeanHellWeek24Product,
    ultimateStrengthChallenge2Product,
    bestOfLeanMethodProduct,
    buildYourStengthMonthProduct,
    resetAndThriveChallengeProduct,
    buildStrengthMonthWeek4Product,
    coreStrengthStabilityProduct,
    leanLifestyleProduct,
    leamMethodLevel5Product,
    summerSurprisePrepProduct,
    powerUpPilatesWeek3Product,
    sunsOutBunsOutProduct,
    lowImpactFatBurnProduct,
    pilatesStrengthFusionProduct,
    ultimateStrengthChallengeProduct,
    matInspiredReformerPilatesProduct,
    weekOfWellnessProduct,
    bestOfLmpeProduct,
    pilatesEvolutionWeek9Product,
    pilatesEvolutionDay50Product,
    pilatesEvolutionWeek7Product,
    pilatesEvolutionWeek6Product,
    pilatesEvolutionWeek5Product,
    leamMethodLevel4Product,
    pilatesEvolutionWeek4Product,
    pilatesEvolutionWeek3Product,
    pilatesEvolutionWeek2Product,
    pilatesEvolutionWeek1Product,
    newYearPrepProduct,
    setYourNewYearGoalsProduct,
    twentyFiveDaysOfLEANMassProduct,
    pilatesBootcampChallengeProduct,
    halloleanHellWeekProduct,
    leamMethodLevel3Product,
    absAndUpperBodyProduct,
    hourGlassShredShapeProduct,
    shredWithWeightsProduct,
    yourStrongestSelfProduct,
    legsSculptFatBurnProduct,
    fatBurnCoreGaloreProduct,
    twentyOneVol3Product,
    leanSecondBirthdayBashProduct,
    leamMethodLevel2Product,
    powerPilatesProduct,
    ultimateWeekOfBattlesProduct,
    ohHiitProduct,
    sculptBurnAndShapeProduct,
    upperBodyAbsAndShredProduct,
    lowerBodyProduct,
    winterGlowUpProduct,
    recoverAndStrengthenPilatesProduct,
    leanTransformationMethodPhase2Product,
    leanTransformationMethodPhase1Product,
    bestOfSculptMonthProduct,
    leanMass2022RepackedProduct,
    cardioGlowUp4WeeksProduct,
    cardioGlowUpProduct,
    busyGirlBlackFriday4WeeksProduct,
    pilatesBodyProduct,
    halloLeanHellWeek22Product,
    halloLeanPrepProduct,
    feelGoodFactory4WeeksProduct,
    gimmeGummyMoreProduct,
    twentyOneVol24WeeksProduct,
    greensAndGlow4WeeksProduct,
    vacayGuide4WeeksProduct,
    birthdayProduct,
    bestOfShredProduct,
    bestOfSculptProduct,
    feelGoodSummer4WeeksProduct,
    periodFriendlyProduct,
    // maryBraunWeek4Product,
    // maryBraunWeek3Product,
    preSummerBootcampProduct,
    deloadPilatesBootCampProduct,
    // maryBraunWeek2Product,
    // maryBraunWeek1Product,
    leanAndStrongTransformationVol24WeeksProduct,
    turnItUpProduct,
    leanAndStrongTransformationVol14WeeksProduct,
    galentineProduct,
    week4Product,
    pilatesWorkoutProduct,
    leanMasWorkoutProduct,
    smallerWaistWorkoutProduct,
    bootyWorkoutProduct,
    hourglassWorkoutProduct,
    sabriWorkoutProduct,
    twentyOneWorkoutProduct,
    halloLeanWorkoutProduct,
    cutStudentWorkoutProduct,
    strongCapableWorkoutProduct,
  ];

  // console.log("GUIDES ------>", guides);

  const featuredClasses = classes?.filter((classObj) => classObj?.asset?.meta_data?.featured);

  // Grouping by subCategory, if any
  const groupBySubCategory = (toGroup) =>
    merge.apply(null, [
      {},
      ...keys(toGroup)?.map((key) => ({
        [key]: groupBy(toGroup[key], "subCategory"),
      })),
    ]) || [];

  const [allClasses, setAllClasses] = useState([]);
  const [allFeaturedClasses, setAllFeaturedClasses] = useState([]);

  useEffect(() => {
    if (!loading) {
      // Filtering out duplicates as many classes are reused
      const flatClasses = uniqBy(
        classes,
        (classData) => `${classData?.asset?.sproutId || classData?.asset?.id} - Current Guide: ${classData?.isCurrentGuide}`
      );
      const cleanedFeaturedClasses = uniqBy(
        featuredClasses,
        (classData) => `${classData?.asset?.sproutId} - Current Guide: ${classData?.isCurrentGuide}`
      );

      // const enhancedFlatClasses = flatClasses.map(classItem => ({
      //     ...classItem,
      //     isCompletedSession: isCompletedSession(classItem)
      // }))
      //
      // const enhancedFeaturedClasses = flatClasses.map(classItem => ({
      //     ...classItem,
      //     isCompletedSession: isCompletedSession(classItem)
      // }))

      // Grouping by course
      const groupedClasses = groupBy(flatClasses, "courseTitle");
      // Grouping by course with only featured classes
      const groupedFeaturedClasses = groupBy(cleanedFeaturedClasses, "courseTitle");

      const groupedClassesBySubCategory = groupBySubCategory(groupedClasses);
      const groupedFeaturedClassesBySubCategory = groupBySubCategory(groupedFeaturedClasses);

      setAllClasses(groupedClassesBySubCategory);
      setAllFeaturedClasses(groupedFeaturedClassesBySubCategory);
    }
  }, [loading]);

  const [searching, setSearching] = useState(false);
  const searchAllClasses = useCallback(
    (searchQuery) => {
      // Using FUSE.js to search all classes
      // TODO: Improve searching by adding filters/sort options and preferably powered by the backend, etc.
      setSearching(true);
      let filteredAllClasses = {};
      keys(allClasses)?.forEach((key) => {
        const courseClasses = flatten(values(allClasses?.[key]));
        const fuse = new Fuse(courseClasses || [], {
          isCaseSensitive: false,
          includeScore: true,
          shouldSort: true,
          minMatchCharLength: 3,
          threshold: 0.6,
          useExtendedSearch: true,
          keys: ["classTitle", "asset.description"],
        });
        const results = fuse?.search(searchQuery)?.map(({ item }) => item) || [];
        filteredAllClasses[key] = uniqBy(results, "asset.sproutId");
      });
      setSearching(false);

      return filteredAllClasses;
    },
    [allClasses]
  );

  /*
    * `classFilters` example structure = [
        [
            { label: WARM_UPS, filter: { workoutType: 'warmUp' } },
            { label: ACTIVATIONS, filter: { workoutType: 'activation' } },
            { label: OTHERS_IN_SESSION, filter: {sessionId: '50000' }}
        ]

        NB: `label` key is optional and NOT required for filtering

      `source` is which dataset is filtered, or it defaults to all classes
    * */
  const getFilteredClasses = useCallback(
    ({ config: { classFilters, source, includeCurrentGuide = false, includeRestDays = false } }) => {
      const filters = [...classFilters];
      let flatClasses = flattenDeep(values(values(source || allClasses))?.map(values));
      flatClasses = orderBy(flatClasses, [(classItem) => classItem?.subCategory?.toLowerCase(), "dayIndex"], ["asc"]);

      let resultFlatClasses = [];

      // console.log("classFilters: ", classFilters);

      filters.forEach((classFilter) => {
        const filteredData = flatClasses.filter(({ asset, productID, sessionId }) => {
          let filterResult = false;
          if (!!classFilter?.filter?.workoutType) {
            filterResult = asset?.meta_data?.workoutType?.indexOf(classFilter?.filter?.workoutType) > -1;
          }

          if (!!classFilter?.filter?.courseProductId) {
            filterResult = parseInt(productID) === parseInt(classFilter?.filter?.courseProductId);
          }

          if (!!classFilter?.filter?.sessionId) {
            filterResult = parseInt(sessionId) === parseInt(classFilter?.filter?.sessionId);
          }
          return filterResult;
        });
        resultFlatClasses.push(filteredData);
      });

      resultFlatClasses = flatten(resultFlatClasses);
      // resultFlatClasses = !!source ? resultFlatClasses : flatClasses; // This essentially reverts all filtering - leaving here incase this intentional in some case but seems incorrect

      if (!includeRestDays) {
        resultFlatClasses = resultFlatClasses?.filter((classItem) => !isRestDay(classItem));
      }

      if (!includeCurrentGuide) {
        resultFlatClasses = resultFlatClasses?.filter(({ isCurrentGuide }) => !isCurrentGuide);
        resultFlatClasses = orderBy(resultFlatClasses, "isCurrentGuide");
      }
      const groupedClasses = groupBy(resultFlatClasses, "courseTitle");
      return groupedClasses;
    },
    [allClasses]
  );

  // assetId is either session_exercise_id for workout based classes (`workout` type product)
  // OR asset_id from actual products of the `course` type
  const getClassByAssetID = (assetId) => {
    if (!classes?.length) return;

    return classes?.find(({ asset }) => parseInt(asset?.id) === parseInt(assetId));
  };

  return (
    <ContentManagerContext.Provider
      value={{
        currentGuideProductID,
        allClasses,
        allFeaturedClasses,
        searchAllClasses,
        getClassByAssetID,
        searching,
        loading,
        guides,
        getFilteredClasses,
        userWorkoutFrequency,
        // totalCompletedSessions
      }}
    >
      {children}
    </ContentManagerContext.Provider>
  );
};

export default ContentManagerProvider;
